/**
 * Master Channel Resolver (Frontend)
 * 
 * Consolidates all channel variants into 4 master channels:
 * - VRBO (#4A6FAE): vrboical, homeaway
 * - Airbnb (#F2BFC0): airbnbOfficial
 * - CLC (#C7E0F4): bookingengine, direct, clcvacationhomes, CLC-Lease
 * - Owner (#D8CFC4): mondayOwnerRes, ownerStay
 * 
 * Colors match CSS tokens (--color-channel-*) as single source of truth.
 * This resolver preserves source channel information while providing
 * consistent master channel metadata for UI display.
 */

/**
 * Resolve raw channel to master channel metadata
 * @param {string} rawChannel - Raw channel string from reservation
 * @param {string} channelSubtype - Optional channel subtype
 * @param {string} status - Optional reservation status (e.g., 'ownerstay')
 * @param {string} clcChannels - Optional custom field clc_channels value (e.g., 'CLC - Lease')
 * @param {Object} canonical - Optional canonical financial summary (preferred source for forced lease)
 * @returns {Object} Master channel metadata
 */
export function resolveMasterChannel(rawChannel, channelSubtype = null, status = null, clcChannels = null, canonical = null) {
  if (!rawChannel || typeof rawChannel !== 'string') {
    // Default fallback to Airbnb
    return {
      master_channel_key: 'airbnb',
      master_channel_label: 'Airbnb',
      master_channel_color: '#F2BFC0', // Matches --color-channel-airbnb
      source_channel_raw: rawChannel || '',
      source_channel_normalized: 'airbnb',
      channel_subtype: null,
    };
  }

  const normalized = rawChannel.toLowerCase().trim();
  
  // HIGHEST PRECEDENCE: Forced Lease by Custom Field
  // If clc_channels="CLC - Lease" OR canonical indicates forced lease, FORCE to CLC Lease
  // This overrides ALL other rules including OwnerStay
  const isForcedLease = (clcChannels && typeof clcChannels === 'string' && clcChannels.trim().toLowerCase() === 'clc - lease') ||
                        (canonical && (canonical.channel_subtype === 'clc_lease' || canonical.payout_method === 'clc_lease_computed'));
  
  if (isForcedLease) {
    return {
      master_channel_key: 'clc',
      master_channel_label: 'CLC',
      master_channel_color: '#C7E0F4', // Matches --color-channel-clc
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized || 'direct',
      channel_subtype: 'clc_lease',
    };
  }
  
  // SECOND PRECEDENCE: Owner Stay Rule (matches backend logic)
  // If channel='direct' AND status='ownerstay' AND clc_channels is blank (NULL or empty/whitespace),
  // then map to owner_stay. This must NOT override lease behavior (lease check above wins).
  // Also check: ANY channel + status="ownerstay" => Owner master channel (legacy behavior)
  const normalizedStatus = status ? status.toLowerCase().trim() : '';
  const normalizedChannel = normalized;
  const isClcChannelsBlank = !clcChannels || (typeof clcChannels === 'string' && clcChannels.trim() === '');
  
  // New rule: direct + ownerstay + blank clc_channels => owner_stay
  if (normalizedChannel === 'direct' && 
      (normalizedStatus === 'ownerstay' || normalizedStatus === 'owner_stay') &&
      isClcChannelsBlank) {
    return {
      master_channel_key: 'owner',
      master_channel_label: 'Owner',
      master_channel_color: '#D8CFC4', // Matches --color-channel-owner
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized || 'ownerstay',
      channel_subtype: channelSubtype || 'owner_stay',
    };
  }
  
  // Legacy rule: ANY channel + status="ownerstay" => Owner master channel
  // This is checked AFTER the new direct+ownerstay rule, so new rule takes precedence
  if (normalizedStatus === 'ownerstay' || normalizedStatus === 'owner_stay') {
    return {
      master_channel_key: 'owner',
      master_channel_label: 'Owner',
      master_channel_color: '#D8CFC4', // Matches --color-channel-owner
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized || 'ownerstay',
      channel_subtype: channelSubtype || 'owner_stay',
    };
  }

  // Master Channel: VRBO (#4A6FAE)
  if (normalized === 'vrboical' || normalized === 'homeaway' || normalized === 'home_away') {
    return {
      master_channel_key: 'vrbo',
      master_channel_label: 'VRBO',
      master_channel_color: '#4A6FAE', // Matches --color-channel-vrbo
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized === 'vrboical' ? 'vrboical' : 'homeaway',
      channel_subtype: channelSubtype || (normalized === 'vrboical' ? 'ical' : 'homeaway'),
    };
  }

  // Master Channel: Airbnb (#F2BFC0)
  if (normalized.includes('airbnb') || normalized === 'airbnbofficial') {
    return {
      master_channel_key: 'airbnb',
      master_channel_label: 'Airbnb',
      master_channel_color: '#F2BFC0', // Matches --color-channel-airbnb
      source_channel_raw: rawChannel,
      source_channel_normalized: 'airbnb',
      channel_subtype: channelSubtype || null,
    };
  }

  // Master Channel: Owner (#D8CFC4)
  if (
    normalized === 'mondayownerres' ||
    normalized === 'monday_owner_res' ||
    normalized === 'ownerstay' ||
    normalized === 'owner_stay' ||
    normalized === 'owner' ||
    normalized === 'owners'
  ) {
    return {
      master_channel_key: 'owner',
      master_channel_label: 'Owner',
      master_channel_color: '#D8CFC4', // Matches --color-channel-owner
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized.includes('monday') ? 'mondayownerres' : 'ownerstay',
      channel_subtype: channelSubtype || (normalized.includes('monday') ? 'monday_owner_res' : 'owner_stay'),
    };
  }

  // Master Channel: CLC (#C7E0F4)
  if (
    normalized === 'bookingengine' ||
    normalized === 'booking_engine' ||
    normalized === 'direct' ||
    normalized.includes('clc') ||
    normalized.includes('clcvacationhomes') ||
    normalized.includes('clc-lease') ||
    normalized.includes('website') ||
    normalized.includes('booking engine')
  ) {
    let clcSubtype = channelSubtype;
    if (!clcSubtype) {
      if (normalized === 'bookingengine' || normalized === 'booking_engine') {
        clcSubtype = 'direct_booking_engine';
      } else if (normalized === 'direct' || normalized.includes('website')) {
        clcSubtype = 'direct';
      } else if (normalized.includes('clc-lease')) {
        clcSubtype = 'clc_lease';
      } else if (normalized.includes('clcvacationhomes')) {
        clcSubtype = 'clcvacationhomes';
      }
    }

    return {
      master_channel_key: 'clc',
      master_channel_label: 'CLC',
      master_channel_color: '#C7E0F4', // Matches --color-channel-clc
      source_channel_raw: rawChannel,
      source_channel_normalized: normalized === 'bookingengine' || normalized === 'booking_engine' 
        ? 'bookingengine' 
        : normalized === 'direct' 
          ? 'direct' 
          : 'clc',
      channel_subtype: clcSubtype,
    };
  }

  // Fallback: Check for VRBO (non-ical, non-homeaway)
  if (normalized.includes('vrbo') || normalized.includes('booking.com') || normalized.includes('bookingcom')) {
    return {
      master_channel_key: 'vrbo',
      master_channel_label: 'VRBO',
      master_channel_color: '#4A6FAE', // Matches --color-channel-vrbo
      source_channel_raw: rawChannel,
      source_channel_normalized: 'vrbo',
      channel_subtype: channelSubtype || 'stripe',
    };
  }

  // Default fallback to Airbnb
  return {
    master_channel_key: 'airbnb',
    master_channel_label: 'Airbnb',
    master_channel_color: '#F2BFC0', // Matches --color-channel-airbnb
    source_channel_raw: rawChannel,
    source_channel_normalized: 'airbnb',
    channel_subtype: null,
  };
}

/**
 * Get master channel key only (lightweight helper)
 * @param {string} rawChannel - Raw channel string
 * @param {string} channelSubtype - Optional channel subtype
 * @param {string} status - Optional reservation status
 * @param {string} clcChannels - Optional custom field clc_channels value
 * @param {Object} canonical - Optional canonical financial summary
 * @returns {string} Master channel key: 'vrbo', 'airbnb', 'clc', 'owner'
 */
export function getMasterChannelKey(rawChannel, channelSubtype = null, status = null, clcChannels = null, canonical = null) {
  return resolveMasterChannel(rawChannel, channelSubtype, status, clcChannels, canonical).master_channel_key;
}
